import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FileText, Plus, Download, Pencil, Save, Trash2, ExternalLink,
  Presentation, Sparkles, Loader2, ChevronDown, RotateCw,
  Paperclip, X, Send, MessagesSquare, FileUp,
  Maximize2, Minimize2, PanelRightClose, PanelRightOpen,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const EXAMPLES = [
  "Draft an invoice for Acme Sports — athlete coverage Q3, $4,200, net-30",
  "Write a one-pager on our concussion protocol for school ADs",
  "A 5-slide pitch deck on Players Health for a school district",
  "A formal letter to a broker confirming Q3 renewal terms",
];

const ACCEPT = ".pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp";

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function Studio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const assistantName = user?.assistant_name || "Andre";
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [composing, setComposing] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("document");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editorBody, setEditorBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exporting, setExporting] = useState("");
  const menuRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);

  // References (uploaded templates / PDFs / docs) + chat iteration
  const [uploads, setUploads] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [revising, setRevising] = useState(false);
  const [pending, setPending] = useState(null); // in-flight user instruction (optimistic)

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  const openInNewTab = () => {
    if (!active) return;
    const blob = new Blob([active.preview_html || ""], { type: "text/html" });
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const refreshList = useCallback(async () => {
    try {
      const r = await api.get("/studio/artifacts");
      setList(r.data);
      return r.data;
    } catch { return []; }
  }, []);

  const refreshUploads = useCallback(async () => {
    try {
      const r = await api.get("/studio/uploads");
      setUploads(r.data);
      return r.data;
    } catch { return []; }
  }, []);

  const selectArtifact = useCallback(async (id) => {
    setComposing(false);
    setEditing(false);
    setInstruction("");
    setPending(null);
    try {
      const r = await api.get(`/studio/artifacts/${id}`);
      setActive(r.data);
      setEditorBody(r.data.html_body || "");
    } catch {
      toast.error("Could not load document.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refreshUploads();
      const items = await refreshList();
      const want = params.get("artifact");
      if (!cancelled && want && items.find((a) => a.id === want)) {
        selectArtifact(want);
      }
    })();
    return () => { cancelled = true; };
  }, [params, refreshUploads, refreshList, selectArtifact]);

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const newIds = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/studio/uploads", fd);
        newIds.push(r.data.id);
      }
      await refreshUploads();
      setSelectedIds((prev) => Array.from(new Set([...prev, ...newIds])));
      toast.success(files.length === 1 ? "Reference attached." : `${files.length} references attached.`);
    } catch {
      toast.error("Upload failed — files must be under 20MB.");
    } finally {
      setUploading(false);
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const removeUpload = async (id) => {
    try {
      await api.delete(`/studio/uploads/${id}`);
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      await refreshUploads();
    } catch {
      toast.error("Could not remove reference.");
    }
  };

  const startNew = (prefill = "", k = "document") => {
    setComposing(true);
    setActive(null);
    setEditing(false);
    setPrompt(prefill);
    setKind(k);
  };

  const generate = async () => {
    if (!prompt.trim() || generating) return;
    setGenerating(true);
    try {
      const r = await api.post("/studio/generate", {
        prompt: prompt.trim(), kind, attachment_ids: selectedIds,
      });
      await refreshList();
      setActive(r.data);
      setEditorBody(r.data.html_body || "");
      setComposing(false);
      setPrompt("");
      toast.success("Draft ready — review before anything is sent.");
    } catch {
      toast.error("Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const revise = async () => {
    if (!active || !instruction.trim() || revising) return;
    const text = instruction.trim();
    setRevising(true);
    setInstruction("");
    setPending(text); // optimistic: show the user's message immediately
    try {
      const r = await api.post(`/studio/artifacts/${active.id}/revise`, {
        instruction: text, attachment_ids: selectedIds,
      });
      setActive(r.data);
      setEditorBody(r.data.html_body || "");
      await refreshList();
    } catch {
      toast.error("Couldn't apply that change. Try rephrasing.");
      setInstruction(text);
    } finally {
      setRevising(false);
      setPending(null);
    }
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const r = await api.put(`/studio/artifacts/${active.id}`, { html_body: editorBody });
      setActive(r.data);
      setEditing(false);
      await refreshList();
      toast.success("Saved.");
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!active) return;
    try {
      await api.delete(`/studio/artifacts/${active.id}`);
      setActive(null);
      setComposing(true);
      await refreshList();
      toast("Document deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const download = async (fmt) => {
    if (!active) return;
    setMenuOpen(false);
    setExporting(fmt);
    try {
      const r = await api.get(`/studio/artifacts/${active.id}/export?fmt=${fmt}`, { responseType: "blob" });
      const ext = fmt === "pdf" ? "pdf" : fmt === "pptx" ? "pptx" : "html";
      const safe = (active.title || "document").replace(/[^a-z0-9_-]+/gi, "_").slice(0, 60) || "document";
      downloadBlob(r.data, `${safe}.${ext}`);
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting("");
    }
  };

  const previewSrc = useMemo(() => {
    if (!active) return "";
    if (editing && active.kind === "document") {
      return (active.preview_html || "").replace(
        /(<main class="ph-content">)[\s\S]*?(<\/main>)/,
        (_m, a, b) => a + editorBody + b
      );
    }
    return active.preview_html || "";
  }, [active, editing, editorBody]);

  return (
    <>
      <div className="surface-header">
        <div>
          <h2 style={{ margin: 0 }}>Studio</h2>
          <div className="text-dim" style={{ fontSize: 13, marginTop: 2 }}>
            Branded documents &amp; decks — drafted, iterated, never sent.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => startNew()} data-testid="studio-new-btn">
          <Plus size={15} /> New document
        </button>
      </div>

      <div className="studio-body" data-testid="studio-surface">
        {/* Rail */}
        <aside className="studio-rail" data-testid="studio-list">
          <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", padding: "2px 4px 10px" }}>
            {list.length} {list.length === 1 ? "document" : "documents"}
          </div>
          {list.length === 0 && (
            <div className="text-dim" style={{ fontSize: 13, padding: 4 }}>No documents yet.</div>
          )}
          {list.map((a) => (
            <button
              key={a.id}
              className={`studio-rail-item ${active?.id === a.id ? "active" : ""}`}
              onClick={() => selectArtifact(a.id)}
              data-testid={`studio-artifact-${a.id}`}
            >
              {a.kind === "deck" ? <Presentation size={15} /> : <FileText size={15} />}
              <span className="studio-rail-meta">
                <span className="studio-rail-title">{a.title}</span>
                <span className="studio-rail-type">{a.doc_type}</span>
              </span>
            </button>
          ))}
        </aside>

        {/* Main */}
        <main className="studio-main">
          {composing || !active ? (
            <Composer
              prompt={prompt} setPrompt={setPrompt}
              kind={kind} setKind={setKind}
              generate={generate} generating={generating}
              uploads={uploads} selectedIds={selectedIds}
              toggleSelect={toggleSelect} removeUpload={removeUpload}
              handleUpload={handleUpload} uploading={uploading}
            />
          ) : (
            <>
              <div className="studio-toolbar">
                <div style={{ minWidth: 0 }}>
                  <div className="studio-doc-type">{active.doc_type}</div>
                  <div className="studio-doc-title">{active.title}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                  {active.kind === "document" && !editing && (
                    <button className="btn" onClick={() => { setEditorBody(active.html_body || ""); setEditing(true); }} data-testid="studio-edit-btn">
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                  {editing && (
                    <>
                      <button className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                      <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="studio-save-btn">
                        {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Save
                      </button>
                    </>
                  )}
                  {!editing && (
                    <>
                      <button className="btn" onClick={() => setExpanded(true)} data-testid="studio-expand-btn" title="Expand to full screen">
                        <Maximize2 size={14} /> Expand
                      </button>
                      <button className="btn btn-ghost" onClick={openInNewTab} data-testid="studio-newtab-btn" title="Open in a new browser tab">
                        <ExternalLink size={14} />
                      </button>
                      <button className="btn btn-ghost" onClick={() => setChatOpen((v) => !v)} data-testid="studio-chat-toggle" title={chatOpen ? "Hide iterate chat" : "Show iterate chat"}>
                        {chatOpen ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
                      </button>
                      <button className="btn" onClick={() => startNew(active.prompt || "", active.kind)} data-testid="studio-regenerate-btn" title="Regenerate from a new brief">
                        <RotateCw size={14} /> Regenerate
                      </button>
                      <div style={{ position: "relative" }} ref={menuRef}>
                        <button className="btn btn-primary" onClick={() => setMenuOpen((v) => !v)} data-testid="studio-download-btn">
                          {exporting ? <Loader2 size={14} className="spin" /> : <Download size={14} />} Download <ChevronDown size={13} />
                        </button>
                        {menuOpen && (
                          <div className="studio-menu" data-testid="studio-download-menu">
                            <button onClick={() => download("pdf")} data-testid="studio-download-pdf">PDF</button>
                            <button onClick={() => download("html")} data-testid="studio-download-html">
                              {active.kind === "deck" ? "HTML deck" : "HTML"}
                            </button>
                            <button onClick={() => download("pptx")} data-testid="studio-download-pptx">PowerPoint (.pptx)</button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="studio-stage">
                {editing && active.kind === "document" ? (
                  <div className="studio-edit-split">
                    <textarea
                      className="studio-source"
                      value={editorBody}
                      onChange={(e) => setEditorBody(e.target.value)}
                      spellCheck={false}
                      data-testid="studio-source"
                    />
                    <iframe title="preview" className="studio-frame" srcDoc={previewSrc} sandbox="allow-same-origin allow-popups" data-testid="studio-preview" />
                  </div>
                ) : (
                  <div className={`studio-work ${chatOpen ? "" : "chat-hidden"}`}>
                    <iframe title="preview" className="studio-frame" srcDoc={previewSrc} sandbox="allow-same-origin allow-popups" data-testid="studio-preview" />
                    {chatOpen && (
                      <ChatPanel
                        assistantName={assistantName}
                        messages={active.messages || []}
                        pending={pending}
                        instruction={instruction} setInstruction={setInstruction}
                        revise={revise} revising={revising}
                        uploads={uploads} selectedIds={selectedIds}
                        toggleSelect={toggleSelect} removeUpload={removeUpload}
                        handleUpload={handleUpload} uploading={uploading}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="studio-footbar">
                {active.vault_doc_id && (
                  <button className="btn btn-ghost" onClick={() => navigate(`/vault/${active.vault_doc_id}`)} data-testid="studio-open-vault-btn">
                    <ExternalLink size={14} /> Open in Vault
                  </button>
                )}
                <button className="btn btn-ghost" onClick={remove} data-testid="studio-delete-btn" style={{ color: "var(--danger)" }}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </>
          )}
        </main>
      </div>

      {expanded && active && (
        <div className="studio-fullscreen" data-testid="studio-fullscreen" onClick={() => setExpanded(false)}>
          <div className="studio-fs-panel" onClick={(e) => e.stopPropagation()}>
            <div className="studio-fs-head">
              <div style={{ minWidth: 0 }}>
                <div className="studio-doc-type">{active.doc_type}</div>
                <div className="studio-doc-title">{active.title}</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                <button className="btn btn-ghost" onClick={openInNewTab} data-testid="studio-fs-newtab" title="Open in a new tab">
                  <ExternalLink size={14} /> New tab
                </button>
                <button className="btn" onClick={() => download("pdf")} disabled={!!exporting} data-testid="studio-fs-pdf">
                  {exporting === "pdf" ? <Loader2 size={14} className="spin" /> : <Download size={14} />} PDF
                </button>
                <button className="btn" onClick={() => download(active.kind === "deck" ? "pptx" : "html")} disabled={!!exporting} data-testid="studio-fs-second">
                  {active.kind === "deck" ? "PPTX" : "HTML"}
                </button>
                <button className="btn btn-ghost" onClick={() => setExpanded(false)} data-testid="studio-fs-close" title="Close (Esc)">
                  <Minimize2 size={14} /> Close
                </button>
              </div>
            </div>
            <div className="studio-fs-stage">
              <iframe title="fullscreen-preview" className="studio-frame" srcDoc={previewSrc} sandbox="allow-same-origin allow-popups" data-testid="studio-fs-preview" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AttachBar({ uploads, selectedIds, toggleSelect, removeUpload, handleUpload, uploading, compact }) {
  const inputRef = useRef(null);
  return (
    <div className={`studio-attach ${compact ? "compact" : ""}`} data-testid="studio-attach">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        style={{ display: "none" }}
        onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
        data-testid="studio-upload-input"
      />
      <button
        type="button"
        className="studio-attach-add"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid="studio-upload-btn"
        title="Attach reference PDFs, Word docs, or templates"
      >
        {uploading ? <Loader2 size={13} className="spin" /> : (compact ? <Paperclip size={13} /> : <FileUp size={13} />)}
        {compact ? "Attach" : "Attach reference files"}
      </button>
      {uploads.map((u) => {
        const on = selectedIds.includes(u.id);
        return (
          <span key={u.id} className={`studio-attach-chip ${on ? "on" : ""}`} data-testid={`studio-ref-${u.id}`}>
            <button type="button" className="studio-attach-pick" onClick={() => toggleSelect(u.id)} title={on ? "Using as reference" : "Click to use as reference"}>
              <Paperclip size={11} /> {u.original_filename}
            </button>
            <button type="button" className="studio-attach-x" onClick={() => removeUpload(u.id)} title="Remove" data-testid={`studio-ref-remove-${u.id}`}>
              <X size={11} />
            </button>
          </span>
        );
      })}
    </div>
  );
}

function ChatPanel({ assistantName, messages, pending, instruction, setInstruction, revise, revising,
                     uploads, selectedIds, toggleSelect, removeUpload, handleUpload, uploading }) {
  const logRef = useRef(null);
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, revising, pending]);

  return (
    <aside className="studio-chat" data-testid="studio-chat">
      <div className="studio-chat-head">
        <MessagesSquare size={14} /> Iterate with {assistantName}
      </div>
      <div className="studio-chat-log" ref={logRef}>
        {messages.length === 0 && !revising && (
          <div className="studio-chat-empty" data-testid="studio-chat-empty">
            {`Tell me what to change and I'll redraft the document — e.g. "make the total $5,200", "add a closing paragraph", or "turn this into a more formal tone."`}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`studio-chat-msg ${m.role}`} data-testid={`studio-chat-msg-${m.role}`}>
            {m.text}
          </div>
        ))}
        {pending && (
          <div className="studio-chat-msg user" data-testid="studio-chat-msg-user">
            {pending}
          </div>
        )}
        {revising && (
          <div className="studio-chat-msg assistant" data-testid="studio-chat-thinking">
            <Loader2 size={13} className="spin" /> Updating the document…
          </div>
        )}
      </div>
      <AttachBar
        uploads={uploads} selectedIds={selectedIds}
        toggleSelect={toggleSelect} removeUpload={removeUpload}
        handleUpload={handleUpload} uploading={uploading} compact
      />
      <div className="studio-chat-input">
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); revise(); } }}
          placeholder="Tell me what to change…"
          rows={2}
          data-testid="studio-chat-textarea"
        />
        <button className="btn btn-primary studio-chat-send" onClick={revise} disabled={!instruction.trim() || revising} data-testid="studio-chat-send">
          {revising ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
        </button>
      </div>
    </aside>
  );
}

function Composer({ prompt, setPrompt, kind, setKind, generate, generating,
                    uploads, selectedIds, toggleSelect, removeUpload, handleUpload, uploading }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="studio-composer fade-up" data-testid="studio-composer">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Sparkles size={18} className="text-cobalt" />
        <h3 style={{ margin: 0 }}>What should I draft?</h3>
      </div>
      <div className="text-dim" style={{ fontSize: 13.5, marginBottom: 18 }}>
        {`Describe the document and I'll produce a branded, editable draft. Attach a template or reference and I'll build off it — then refine it in chat.`}
      </div>

      <div className="studio-kind-toggle">
        <button
          className={kind === "document" ? "active" : ""}
          onClick={() => setKind("document")}
          data-testid="studio-kind-document"
        >
          <FileText size={15} /> Document
        </button>
        <button
          className={kind === "deck" ? "active" : ""}
          onClick={() => setKind("deck")}
          data-testid="studio-kind-deck"
        >
          <Presentation size={15} /> Presentation
        </button>
      </div>

      <textarea
        ref={ref}
        className="field studio-prompt"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) generate(); }}
        placeholder="e.g. Draft an invoice for Acme Sports — athlete coverage Q3, $4,200, net-30"
        rows={4}
        data-testid="studio-prompt"
      />

      <AttachBar
        uploads={uploads} selectedIds={selectedIds}
        toggleSelect={toggleSelect} removeUpload={removeUpload}
        handleUpload={handleUpload} uploading={uploading}
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
        {EXAMPLES.map((ex) => (
          <button key={ex} className="chip" onClick={() => setPrompt(ex)} style={{ fontSize: 12.5 }}>
            {ex}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 22 }}>
        <button className="btn btn-primary" onClick={generate} disabled={!prompt.trim() || generating} data-testid="studio-generate-btn">
          {generating ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
          {generating ? "Drafting…" : "Generate draft"}
        </button>
        <span className="text-dim" style={{ fontSize: 12.5 }}>
          ⌘/Ctrl + Enter · Nothing is sent — drafts only.
        </span>
      </div>
    </div>
  );
}
